/**
 * Motif sprites for the detail layer (C0-SKILLFX): bamboo leaves, reed fluff, cloud and mist puffs, beads (blood,
 * ink) and flecks, two or three tones of an element ramp, hard pixels, cached as small canvases so a particle costs
 * one drawImage. Two grids (render/density.ts): G2 sprites (half an art pixel a cell) for what clings to a figure,
 * G1 (a whole art pixel a cell) for what belongs to the world; on the 1x tiers everything is G1.
 */
import { canvas, ctx2d } from '../../pixel';
import { density1x } from '../../density';
import { RES } from '../brush';
import { RAMPS, type Ramp, type RampId } from './ramps';

export type Motif = 'leaf' | 'fluff' | 'puff' | 'mistPuff' | 'bead' | 'fleck';
export type Grid = 'fig' | 'world';

/** Art px per sprite cell on a grid: half a pixel for figure effects under option C, else one. */
export const cellOf = (grid: Grid): number => (grid === 'fig' && !density1x() ? 1 / RES : 1);
/** Snaps a coordinate to a grid. */
export const snap = (v: number, grid: Grid): number => {
  const c = cellOf(grid);
  return Math.round(v / c) * c;
};

const CACHE = new Map<string, HTMLCanvasElement>();

/** Paints `n`×`n` cells (null: empty) into a canvas whose cell is `cell` art px (drawn at canvas px per RES). */
function build(n: number, grid: Grid, paint: (x: number, y: number) => string | null): HTMLCanvasElement {
  const k = cellOf(grid) * RES;
  const c = canvas(n * k, n * k);
  const g = ctx2d(c);
  for (let y = 0; y < n; y++)
    for (let x = 0; x < n; x++) {
      const col = paint(x, y);
      if (!col) continue;
      g.fillStyle = col;
      g.fillRect(x * k, y * k, k, k);
    }
  return c;
}

/** Eight rotations of a leaf / streak shape. */
export const ANGLES = 8;

/**
 * A motif sprite: `size` cells across (leaf: its length), `rot` 0–7 (eighths of a half turn, leaves only),
 * `fade` 0–3 dither steps (puffs dissolve by dropping cells, never by alpha).
 */
export function motif(m: Motif, ramp: RampId | Ramp, size: number, grid: Grid, rot = 0, fade = 0): HTMLCanvasElement {
  const R = typeof ramp === 'string' ? RAMPS[ramp] : ramp;
  const key = `${m}|${R.join('')}|${size}|${grid}|${rot}|${fade}|${density1x() ? 1 : 0}`;
  let c = CACHE.get(key);
  if (c) return c;
  const drop = (x: number, y: number): boolean => fade > 0 && ((x * 7 + y * 13 + (x ^ y)) & 3) < fade;
  switch (m) {
    case 'leaf': {
      // A bamboo leaf: a long thin lens with a dark rim, a lit midrib and a pale tip, rotated.
      const L = size;
      const W = Math.max(2, size * 0.38);
      const a = (rot / ANGLES) * Math.PI;
      const ca = Math.cos(a);
      const sa = Math.sin(a);
      const n = L + 2;
      c = build(n, grid, (x, y) => {
        const dx = x + 0.5 - n / 2;
        const dy = y + 0.5 - n / 2;
        const u = (dx * ca + dy * sa) / (L / 2);
        const v = (-dx * sa + dy * ca) / (W / 2);
        if (Math.abs(u) > 1) return null;
        const half = Math.sqrt(1 - u * u) * (u > 0 ? 1 - u * 0.35 : 1);
        if (Math.abs(v) > half) return null;
        if (drop(x, y)) return null;
        if (Math.abs(v) > half - 0.55) return R[0];
        if (u > 0.55) return R[4];
        return Math.abs(v) < 0.3 ? R[3] : v < 0 ? R[2] : R[1];
      });
      break;
    }
    case 'fluff': {
      // Reed fluff: a pale cross of `size` arms with a lit core and wisps on the diagonals.
      const n = size * 2 + 1;
      const m = size;
      c = build(n, grid, (x, y) => {
        const dx = Math.abs(x - m);
        const dy = Math.abs(y - m);
        if (dx === 0 && dy === 0) return R[4];
        if ((dx === 0 || dy === 0) && dx + dy <= size) return drop(x, y) ? null : dx + dy === size ? R[2] : R[3];
        if (dx === dy && dx === 1) return R[3];
        return null;
      });
      break;
    }
    case 'puff':
    case 'mistPuff': {
      // A cloud puff: dark rim below, light body, lit upper right; mist is checkered (half the cells) and paler.
      const n = size * 2 + 1;
      const r = size + 0.4;
      c = build(n, grid, (x, y) => {
        const dx = x + 0.5 - n / 2;
        const dy = y + 0.5 - n / 2;
        const d = Math.hypot(dx, dy * 1.15);
        if (d > r) return null;
        if (drop(x, y)) return null;
        if (m === 'mistPuff' && (x + y) % 2 === 1 && d > r * 0.45) return null;
        const lit = dx > 0 && dy < 0 && d > r * 0.35;
        if (d > r - 1) return dy > 0 ? R[1] : m === 'mistPuff' ? R[3] : R[2];
        return lit ? R[4] : dy > r * 0.3 ? R[2] : R[3];
      });
      break;
    }
    case 'bead': {
      // A bead (blood, ink): dark rim, body, a glint on the upper right.
      const n = size * 2 + 1;
      const r = size + 0.3;
      c = build(n, grid, (x, y) => {
        const dx = x + 0.5 - n / 2;
        const dy = y + 0.5 - n / 2;
        const d = Math.hypot(dx, dy);
        if (d > r) return null;
        if (drop(x, y)) return null;
        if (d > r - 1) return R[0];
        return dx > 0 && dy < 0 && size > 1 ? R[4] : R[2];
      });
      break;
    }
    case 'fleck': {
      c = build(size, grid, (x, y) => (drop(x, y) ? null : (x + y) % 2 ? R[3] : R[4]));
      break;
    }
  }
  CACHE.set(key, c);
  return c;
}

/** Draws a motif centred at (x, y) art px, snapped to its grid. */
export function drawMotif(
  g: CanvasRenderingContext2D,
  m: Motif,
  ramp: RampId | Ramp,
  size: number,
  grid: Grid,
  x: number,
  y: number,
  rot = 0,
  fade = 0,
): void {
  const c = motif(m, ramp, size, grid, ((rot % ANGLES) + ANGLES) % ANGLES, fade);
  const w = c.width / RES;
  const h = c.height / RES;
  g.drawImage(c, snap(x - w / 2, grid), snap(y - h / 2, grid), w, h);
}

/** A one-cell dot on a grid. */
export function dot(g: CanvasRenderingContext2D, col: string, x: number, y: number, grid: Grid, cells = 1): void {
  const c = cellOf(grid);
  g.fillStyle = col;
  g.fillRect(snap(x, grid), snap(y, grid), c * cells, c * cells);
}
